import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Calendar,
  Plus,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  UserPlus,
  ThumbsUp,
  ThumbsDown,
  Ban,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import RecordNavigator from '../../components/ui/record-navigator';
import SearchBuilder, { applyFilters } from '../../components/search/SearchBuilder';
import useTableColumns from '@/hooks/useTableColumns';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';
import api from '@/lib/axios';
import { formatClocks, formatDate, formatDateTime, formatTime } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';
import DateInput from '@/components/ui/date-input';

// Define filterable fields
const FILTER_FIELDS = [
  { key: 'name', label: 'Bezeichnung', type: 'text' },
  { key: 'participant_name', label: 'Teilnehmer', type: 'text' },
  { key: 'teacher_name', label: 'Lehrer', type: 'text' },
  { key: 'status', label: 'Status', type: 'select', options: [
    { value: 'open', label: 'Offen' },
    { value: 'reserved', label: 'Reserviert' },
    { value: 'completed', label: 'Durchgeführt' },
    { value: 'converted', label: 'Mit Folgebelegung' },
    { value: 'not_converted', label: 'Ohne Folgebelegung' },
    { value: 'cancelled', label: 'Abgesagt' },
  ]},
];

// Define columns
const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Bezeichnung', visible: true },
  { key: 'participant', label: 'Teilnehmer', visible: true },
  { key: 'date', label: 'Termin', visible: true },
  { key: 'teacher', label: 'Lehrer', visible: true },
  { key: 'subject', label: 'Fach/Kurs', visible: true },
  { key: 'status', label: 'Status', visible: true },
];

// Termin sortiert nach Datum plus Uhrzeit, damit ein Tag in Stundenfolge steht.
const LESSON_SORT_ACCESSORS = {
  name: (l) => l.name,
  participant: (l) => l.participant_name,
  date: (l) => (l.scheduled_date ? `${l.scheduled_date} ${formatTime(l.scheduled_time) || ''}`.trim() : ''),
  teacher: (l) => l.teacher_name,
  subject: (l) => l.subject_name || l.course_name,
  status: (l) => l.status,
};

const TrialLessonsPage = () => {
  const [allLessons, setAllLessons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([]);
  const { columns, setColumns } = useTableColumns('trial-lessons-list', DEFAULT_COLUMNS);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [smsLesson, setSmsLesson] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [smsLogs, setSmsLogs] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  
  // Master data
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  
  // Form data
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 30,
    teacher_id: '',
    subject_id: '',
    course_id: '',
  });
  
  const [reserveForm, setReserveForm] = useState({
    student_id: '',
    prospect_first_name: '',
    prospect_last_name: '',
    prospect_email: '',
    prospect_phone: '',
    prospect_birth_date: '',
    guardian_name: '',
    guardian_email: '',
    guardian_phone: '',
    notes: '',
    is_existing_student: false,
  });
  
  const [completeForm, setCompleteForm] = useState({
    feedback: '',
    internal_notes: '',
    converted: false,
  });

  useEffect(() => {
    fetchData();
    fetchMasterData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statsRes = await api.get('/trial-lessons/stats');
      setStats(statsRes.data);
      
      let allData = [];
      let offset = 0;
      const batchSize = 100;
      
      while (true) {
        const response = await api.get(`/trial-lessons?limit=${batchSize}&offset=${offset}`);
        const batch = Array.isArray(response.data) ? response.data : (response.data?.items || []);
        if (!batch || batch.length === 0) break;
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        offset += batchSize;
      }
      
      setAllLessons(allData);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (error?.message || 'Unbekannter Fehler');
      toast.error(`Fehler beim Laden der Daten: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [studentsRes, teachersRes, subjectsRes, coursesRes] = await Promise.all([
        api.get('/students?limit=100'),
        api.get('/teachers?limit=100'),
        api.get('/subjects'),
        api.get('/courses?limit=100'),
      ]);
      setStudents(studentsRes.data || []);
      setTeachers(teachersRes.data || []);
      setSubjects(subjectsRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Stammdaten:', error);
    }
  };

  const { sort, toggleSort, sortRows } = useTableSort(LESSON_SORT_ACCESSORS, { key: 'date', dir: 'desc' });

  const filteredLessons = useMemo(() => {
    const searchFields = ['name', 'participant_name', 'teacher_name', 'subject_name'];
    return sortRows(applyFilters(allLessons, filters, searchTerm, searchFields));
  }, [allLessons, filters, searchTerm, sortRows]);

  const handleNavigate = (newIndex) => {
    setCurrentIndex(newIndex);
    const row = document.querySelector(`[data-testid="lesson-row-${filteredLessons[newIndex]?.id}"]`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('bg-blue-50');
      setTimeout(() => row.classList.remove('bg-blue-50'), 2000);
    }
  };

  const createLesson = async () => {
    try {
      const data = {
        ...createForm,
        subject_id: createForm.subject_id || null,
        scheduled_date: createForm.scheduled_date || null,
        scheduled_time: createForm.scheduled_time || null,
      };
      
      await api.post('/trial-lessons', data);
      toast.success('Probestunde erstellt');
      setShowCreateDialog(false);
      resetCreateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const reserveLesson = async () => {
    if (!selectedLesson) return;
    
    try {
      const data = reserveForm.is_existing_student
        ? { student_id: reserveForm.student_id }
        : {
            prospect_first_name: reserveForm.prospect_first_name,
            prospect_last_name: reserveForm.prospect_last_name,
            prospect_email: reserveForm.prospect_email,
            prospect_phone: reserveForm.prospect_phone,
            prospect_birth_date: reserveForm.prospect_birth_date || null,
            guardian_name: reserveForm.guardian_name,
            guardian_email: reserveForm.guardian_email,
            guardian_phone: reserveForm.guardian_phone,
            notes: reserveForm.notes,
          };
      
      await api.post(`/trial-lessons/${selectedLesson.id}/reserve`, data);
      toast.success('Probestunde reserviert');
      setShowReserveDialog(false);
      setSelectedLesson(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Reservieren');
    }
  };

  const completeLesson = async () => {
    if (!selectedLesson) return;
    
    try {
      await api.post(`/trial-lessons/${selectedLesson.id}/complete`, completeForm);
      toast.success('Probestunde abgeschlossen');
      setShowCompleteDialog(false);
      setSelectedLesson(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Abschließen');
    }
  };

  const cancelLesson = async (lesson) => {
    if (!window.confirm('Probestunde wirklich absagen?')) return;
    
    try {
      await api.post(`/trial-lessons/${lesson.id}/cancel`);
      toast.success('Probestunde abgesagt');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Absagen');
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      description: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 30,
      teacher_id: '',
      subject_id: '',
      course_id: '',
    });
  };

  const openReserveDialog = (lesson) => {
    setSelectedLesson(lesson);
    setReserveForm({
      student_id: '',
      prospect_first_name: '',
      prospect_last_name: '',
      prospect_email: '',
      prospect_phone: '',
      prospect_birth_date: '',
      guardian_name: '',
      guardian_email: '',
      guardian_phone: '',
      notes: '',
      is_existing_student: false,
    });
    setShowReserveDialog(true);
  };

  const openCompleteDialog = (lesson) => {
    setSelectedLesson(lesson);
    setCompleteForm({
      feedback: '',
      internal_notes: '',
      converted: false,
    });
    setShowCompleteDialog(true);
  };

  const openSmsDialog = async (lesson) => {
    setSmsLesson(lesson);
    setSmsResult(null);
    setSmsLogs([]);
    setShowSmsDialog(true);
    // Hydrate detail (list payload doesn't include guardian_phone / prospect_phone)
    try {
      const detail = await api.get(`/trial-lessons/${lesson.id}`);
      setSmsLesson({ ...lesson, ...detail.data });
    } catch (_e) { /* ignore — fallback to list payload */ }
    try {
      const r = await api.get(`/admin/trial-lessons/${lesson.id}/sms-logs`, { params: { limit: 10 } });
      setSmsLogs(r.data?.items || []);
    } catch (_e) { /* ignore */ }
  };

  const sendSmsReminder = async (force = false) => {
    if (!smsLesson) return;
    setSmsSending(true);
    try {
      const r = await api.post(`/admin/trial-lessons/${smsLesson.id}/send-sms-reminder`, null, {
        params: force ? { force: true } : {},
      });
      setSmsResult(r.data);
      if (r.data.sent > 0) {
        toast.success(`${r.data.sent} SMS-Erinnerung(en) versendet`);
      } else if (r.data.skipped > 0) {
        toast.info(`${r.data.skipped} SMS geskippt (Throttle aktiv). Nutze „Trotzdem senden" für Notfall-Re-Send.`);
      } else if (r.data.reason === 'keine_telefonnummer') {
        toast.error('Keine Telefonnummern hinterlegt');
      } else {
        toast.error(`${r.data.failed} SMS fehlgeschlagen`);
      }
      // reload log
      const r2 = await api.get(`/admin/trial-lessons/${smsLesson.id}/sms-logs`, { params: { limit: 10 } });
      setSmsLogs(r2.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'SMS-Versand fehlgeschlagen');
    } finally {
      setSmsSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { label: 'Offen', icon: Clock, className: 'bg-blue-50 text-blue-700 border-blue-200' },
      reserved: { label: 'Reserviert', icon: UserPlus, className: 'bg-amber-50 text-amber-700 border-amber-200' },
      completed: { label: 'Durchgeführt', icon: CheckCircle2, className: 'bg-slate-100 text-slate-700' },
      converted: { label: 'Mit Folgebelegung', icon: ThumbsUp, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      not_converted: { label: 'Ohne Folgebelegung', icon: ThumbsDown, className: 'bg-red-50 text-red-700 border-red-200' },
      cancelled: { label: 'Abgesagt', icon: Ban, className: 'bg-slate-100 text-slate-500' },
    };
    const config = variants[status] || variants.open;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const isColumnVisible = (key) => {
    const col = columns.find(c => c.key === key);
    return col ? col.visible : true;
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="trial-lessons-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">Probe-/Schnupperstunden</h1>
            <p className="text-slate-600">
              {filteredLessons.length} von {allLessons.length} Probestunden
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="create-lesson-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Probestunde
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Gesamt</p>
                    <p className="text-xl font-bold">{stats.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Offen</p>
                    <p className="text-xl font-bold text-blue-600">{stats.open}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <UserPlus className="w-5 h-5 text-brand-red" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Reserviert</p>
                    <p className="text-xl font-bold text-brand-red">{stats.reserved}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Durchgeführt</p>
                    <p className="text-xl font-bold">{stats.completed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <ThumbsUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Konvertiert</p>
                    <p className="text-xl font-bold text-emerald-600">{stats.converted}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ThumbsDown className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Nicht konv.</p>
                    <p className="text-xl font-bold text-red-600">{stats.not_converted}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-r from-brand-red/5 to-slate-50">
              <CardContent className="p-4">
                <div>
                  <p className="text-xs text-gray-500">Konversionsrate</p>
                  <p className="text-xl font-bold text-brand-red">{stats.conversion_rate}%</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SearchBuilder */}
        <SearchBuilder
          fields={FILTER_FIELDS}
          onFilterChange={setFilters}
          onSearch={setSearchTerm}
          columns={columns}
          onColumnsChange={setColumns}
          placeholder="Probestunden suchen..."
          className="mb-4"
        />

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              {filteredLessons.length} Ergebnisse
            </span>
            
            {filteredLessons.length > 0 && (
              <RecordNavigator
                currentIndex={currentIndex}
                totalCount={filteredLessons.length}
                onNavigate={handleNavigate}
                records={filteredLessons}
                getRecordLabel={(lesson) => lesson.name}
              />
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="lessons-table">
              <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  {DEFAULT_COLUMNS.filter((col) => isColumnVisible(col.key)).map((col) => (
                    <SortableTh
                      key={col.key}
                      sortKey={col.key}
                      sort={sort}
                      onSort={toggleSort}
                      testIdPrefix="lessons-sort"
                    >
                      {col.label}
                    </SortableTh>
                  ))}
                  <th className="p-4 text-left">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredLessons.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500">
                      Keine Probestunden gefunden
                    </td>
                  </tr>
                ) : (
                  filteredLessons.map((lesson) => (
                    <tr 
                      key={lesson.id} 
                      data-testid={`lesson-row-${lesson.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      {isColumnVisible('name') && (
                        <td className="p-4 text-sm text-slate-700 font-medium">{formatClocks(lesson.name) || lesson.name}</td>
                      )}
                      {isColumnVisible('participant') && (
                        <td className="p-4 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            {lesson.is_prospect && (
                              <Badge variant="outline" className="text-xs">Interessent</Badge>
                            )}
                            {lesson.participant_name || '-'}
                          </div>
                        </td>
                      )}
                      {isColumnVisible('date') && (
                        <td className="p-4 text-sm text-slate-600">
                          {lesson.scheduled_date ? (
                            <div>
                              <div>{formatDate(lesson.scheduled_date)}</div>
                              {lesson.scheduled_time && (
                                <div className="text-xs text-slate-400">{formatTime(lesson.scheduled_time)} Uhr</div>
                              )}
                            </div>
                          ) : '-'}
                        </td>
                      )}
                      {isColumnVisible('teacher') && (
                        <td className="p-4 text-sm text-slate-600">{lesson.teacher_name || '-'}</td>
                      )}
                      {isColumnVisible('subject') && (
                        <td className="p-4 text-sm text-slate-600">
                          {formatClocks(lesson.subject_name || lesson.course_name) || lesson.subject_name || lesson.course_name || '-'}
                        </td>
                      )}
                      {isColumnVisible('status') && (
                        <td className="p-4 text-sm">{getStatusBadge(lesson.status)}</td>
                      )}
                      <td className="p-4 text-sm">
                        <div className="flex gap-2">
                          {(lesson.status === 'open' || lesson.status === 'reserved') && lesson.scheduled_date && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation?.(); openSmsDialog(lesson); }}
                              className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                              title="SMS-Erinnerung senden"
                              data-testid={`sms-reminder-${lesson.id}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                          )}
                          {lesson.status === 'open' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReserveDialog(lesson)}
                              data-testid={`reserve-${lesson.id}`}
                            >
                              Reservieren
                            </Button>
                          )}
                          {lesson.status === 'reserved' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openCompleteDialog(lesson)}
                                data-testid={`complete-${lesson.id}`}
                              >
                                Abschließen
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => cancelLesson(lesson)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Neue Probestunde</DialogTitle>
              <DialogDescription>
                Erstellen Sie eine neue Probe- oder Schnupperstunde
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Bezeichnung *</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  placeholder="z.B. Schnupperstunde Klavier"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Datum</Label>
                  <DateInput
                    
                    value={createForm.scheduled_date}
                    onChange={(e) => setCreateForm({...createForm, scheduled_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Uhrzeit</Label>
                  <Input
                    type="time"
                    value={formatTime(createForm.scheduled_time) || ''}
                    onChange={(e) => setCreateForm({...createForm, scheduled_time: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Dauer</Label>
                <Select 
                  value={String(createForm.duration_minutes)} 
                  onValueChange={(v) => setCreateForm({...createForm, duration_minutes: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 Min</SelectItem>
                    <SelectItem value="45">45 Min</SelectItem>
                    <SelectItem value="60">60 Min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Lehrer</Label>
                <Select 
                  value={createForm.teacher_id} 
                  onValueChange={(v) => setCreateForm({...createForm, teacher_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Lehrer auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.first_name} {t.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Fach</Label>
                <Select 
                  value={createForm.subject_id} 
                  onValueChange={(v) => setCreateForm({...createForm, subject_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Fach auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Beschreibung</Label>
                <Textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  placeholder="Optionale Beschreibung"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={createLesson} disabled={!createForm.name}>
                Erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reserve Dialog */}
        <Dialog open={showReserveDialog} onOpenChange={setShowReserveDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Probestunde reservieren</DialogTitle>
              <DialogDescription>
                {selectedLesson?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="flex gap-4 p-3 bg-slate-50 rounded-lg">
                <Button
                  variant={reserveForm.is_existing_student ? "default" : "outline"}
                  size="sm"
                  onClick={() => setReserveForm({...reserveForm, is_existing_student: true})}
                >
                  Bestehender Schüler
                </Button>
                <Button
                  variant={!reserveForm.is_existing_student ? "default" : "outline"}
                  size="sm"
                  onClick={() => setReserveForm({...reserveForm, is_existing_student: false})}
                >
                  Neuer Interessent
                </Button>
              </div>
              
              {reserveForm.is_existing_student ? (
                <div>
                  <Label>Schüler auswählen *</Label>
                  <Select 
                    value={reserveForm.student_id} 
                    onValueChange={(v) => setReserveForm({...reserveForm, student_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Schüler auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.first_name} {s.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Vorname *</Label>
                      <Input
                        value={reserveForm.prospect_first_name}
                        onChange={(e) => setReserveForm({...reserveForm, prospect_first_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Nachname *</Label>
                      <Input
                        value={reserveForm.prospect_last_name}
                        onChange={(e) => setReserveForm({...reserveForm, prospect_last_name: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>E-Mail</Label>
                      <Input
                        type="email"
                        value={reserveForm.prospect_email}
                        onChange={(e) => setReserveForm({...reserveForm, prospect_email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input
                        value={reserveForm.prospect_phone}
                        onChange={(e) => setReserveForm({...reserveForm, prospect_phone: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Geburtsdatum</Label>
                    <DateInput
                      
                      value={reserveForm.prospect_birth_date}
                      onChange={(e) => setReserveForm({...reserveForm, prospect_birth_date: e.target.value})}
                    />
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Eltern/Erziehungsberechtigte</p>
                    <div className="space-y-3">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={reserveForm.guardian_name}
                          onChange={(e) => setReserveForm({...reserveForm, guardian_name: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>E-Mail</Label>
                          <Input
                            type="email"
                            value={reserveForm.guardian_email}
                            onChange={(e) => setReserveForm({...reserveForm, guardian_email: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Telefon</Label>
                          <Input
                            value={reserveForm.guardian_phone}
                            onChange={(e) => setReserveForm({...reserveForm, guardian_phone: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              <div>
                <Label>Notizen</Label>
                <Textarea
                  value={reserveForm.notes}
                  onChange={(e) => setReserveForm({...reserveForm, notes: e.target.value})}
                  placeholder="Optionale Notizen"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReserveDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={reserveLesson}
                disabled={reserveForm.is_existing_student 
                  ? !reserveForm.student_id 
                  : (!reserveForm.prospect_first_name || !reserveForm.prospect_last_name)}
              >
                Reservieren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Dialog */}
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Probestunde abschließen</DialogTitle>
              <DialogDescription>
                {selectedLesson?.name} - {selectedLesson?.participant_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Feedback (für Interessent)</Label>
                <Textarea
                  value={completeForm.feedback}
                  onChange={(e) => setCompleteForm({...completeForm, feedback: e.target.value})}
                  placeholder="Öffentliches Feedback"
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Interne Notizen</Label>
                <Textarea
                  value={completeForm.internal_notes}
                  onChange={(e) => setCompleteForm({...completeForm, internal_notes: e.target.value})}
                  placeholder="Interne Notizen (nicht sichtbar für Interessent)"
                  rows={2}
                />
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-3">Ergebnis der Probestunde</p>
                <div className="flex gap-4">
                  <Button
                    variant={completeForm.converted ? "default" : "outline"}
                    className={completeForm.converted ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    onClick={() => setCompleteForm({...completeForm, converted: true})}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Mit Folgebelegung
                  </Button>
                  <Button
                    variant={!completeForm.converted ? "default" : "outline"}
                    className={!completeForm.converted ? "bg-red-600 hover:bg-red-700" : ""}
                    onClick={() => setCompleteForm({...completeForm, converted: false})}
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    Ohne Folgebelegung
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={completeLesson}>
                Abschließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SMS-Erinnerung Dialog */}
        <Dialog open={showSmsDialog} onOpenChange={(o) => { if (!o) setShowSmsDialog(false); }}>
          <DialogContent className="max-w-2xl" data-testid="sms-reminder-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-sky-600" />
                SMS-Erinnerung zur Probestunde
              </DialogTitle>
              <DialogDescription>
                {smsLesson?.name} — {smsLesson?.participant_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-xs uppercase text-slate-500 mb-1">Termin</div>
                  <div className="font-medium">
                    {smsLesson?.scheduled_date ? formatDate(smsLesson.scheduled_date) : '—'}
                    {smsLesson?.scheduled_time ? ` · ${formatTime(smsLesson.scheduled_time)} Uhr` : ''}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-xs uppercase text-slate-500 mb-1">Empfänger</div>
                  <div className="text-xs space-y-0.5">
                    {smsLesson?.guardian_phone && <div>📱 Zahler: <span className="font-mono">{smsLesson.guardian_phone}</span></div>}
                    {smsLesson?.prospect_phone && <div>📱 Interessent: <span className="font-mono">{smsLesson.prospect_phone}</span></div>}
                    {!smsLesson?.guardian_phone && !smsLesson?.prospect_phone && (
                      <div className="text-rose-600">Keine Telefonnummer hinterlegt</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-xs text-sky-900">
                <strong>Hinweis:</strong> Standardmäßig wird eine Erinnerungs-SMS nur einmal pro Empfänger innerhalb von 24 h versendet (Throttle-Schutz). Bei Notfällen kannst du „Trotzdem senden" nutzen.
              </div>

              {smsResult && (
                <div className="p-3 rounded-lg border bg-white" data-testid="sms-reminder-result">
                  <div className="text-xs uppercase text-slate-500 mb-1">Ergebnis</div>
                  <div className="text-sm">
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 mr-2">{smsResult.sent} gesendet</Badge>
                    {smsResult.skipped > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-brand-red/10 mr-2">{smsResult.skipped} geskippt</Badge>}
                    {smsResult.failed > 0 && <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">{smsResult.failed} fehlgeschlagen</Badge>}
                  </div>
                </div>
              )}

              {smsLogs.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="sms-reminder-log">
                  <div className="px-3 py-2 bg-slate-50 text-xs uppercase text-slate-500">Bisherige Sendungen</div>
                  <div className="max-h-44 overflow-y-auto">
                    {smsLogs.map((log) => (
                      <div key={log.id} className="px-3 py-2 border-t border-slate-100 text-xs flex items-center justify-between">
                        <div>
                          <span className="font-mono">{log.recipient}</span>
                          {log.bird_message_id && <span className="ml-2 text-slate-400 font-mono">{log.bird_message_id.slice(0, 8)}…</span>}
                          {log.delivered_at && (
                            <span className="ml-2 text-emerald-700 font-medium">
                              ✓ zugestellt {formatTime(log.delivered_at)}
                            </span>
                          )}
                          {log.failed_at && (
                            <span className="ml-2 text-rose-700 font-medium">
                              ✗ fehlgeschlagen {formatTime(log.failed_at)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">{formatDateTime(log.created_at)}</span>
                          <Badge className={
                            log.status === 'delivered' ? 'bg-emerald-600 text-white hover:bg-emerald-600' :
                            log.status === 'sent' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' :
                            log.status === 'skipped_throttle' ? 'bg-amber-100 text-amber-800 hover:bg-brand-red/10' :
                            log.status === 'blocked_denylist' ? 'bg-slate-200 text-slate-700 hover:bg-slate-200' :
                            'bg-rose-100 text-rose-800 hover:bg-rose-100'
                          }>{statusLabel(log.status)}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowSmsDialog(false)}>Schließen</Button>
              {smsResult && smsResult.skipped > 0 && (
                <Button
                  variant="outline"
                  onClick={() => sendSmsReminder(true)}
                  disabled={smsSending}
                  data-testid="sms-reminder-force-btn"
                  className="border-brand-red/40 text-amber-700 hover:bg-brand-red/5"
                >
                  {smsSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1" />}
                  Trotzdem senden
                </Button>
              )}
              <Button
                onClick={() => sendSmsReminder(false)}
                disabled={smsSending || (!smsLesson?.guardian_phone && !smsLesson?.prospect_phone)}
                data-testid="sms-reminder-send-btn"
                className="bg-sky-600 hover:bg-sky-700"
              >
                {smsSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1" />}
                SMS-Erinnerung senden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TrialLessonsPage;
